import { useState } from 'react';
import { useGenerateCustomToken } from './useUser';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useMSALLogin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const generateTokenMutation = useGenerateCustomToken();

    const checkUserInFirebase = async (email) => {
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email_address', '==', email));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const userDoc = querySnapshot.docs[0];
                return {
                    exists: true,
                    uid: userDoc.id,
                    userData: userDoc.data()
                };
            }
            return { exists: false };
        } catch (err) {
            console.error('Error checking user in Firebase:', err);
            throw err;
        }
    };

    const handleMSALLoginSuccess = async (payload) => {
        setLoading(true);
        setError(null);

        try {
            const account = payload.account;
            if (!account) {
                const error = 'No account information received';
                console.error(error);
                setError(error);
                return { error };
            }

            const userEmail = account.username;

            if (!userEmail) {
                const error = 'No email information received';
                console.error(error);
                setError(error);
                return { error };
            }

            const userExists = await checkUserInFirebase(userEmail);

            if (userExists.exists) {
                try {
                    // Generate custom token using the mutation
                    const result = await generateTokenMutation.mutateAsync(userEmail);
                    
                    if (result.error) {
                        throw new Error(result.error);
                    }
                    
                    // Sign in to Firebase with the custom token
                    await signInWithCustomToken(auth, result.token);
                    
                    return { 
                        success: true,
                        user: { ...userExists.userData, uid: userExists.uid }
                    };
                } catch (tokenError) {
                    console.error('Error generating or using token:', tokenError);
                    setError(tokenError.message || 'Failed to generate authentication token');
                    return { error: tokenError.message || 'Failed to generate authentication token' };
                }
            } else {
                const error = 'User not found. Please contact administration to get access';
                setError(error);
                return { error };
            }
        } catch (err) {
            console.error('Error in handleMSALLoginSuccess:', err);
            const error = err.message || 'An error occurred during login';
            setError(error);
            return { error };
        } finally {
            setLoading(false);
        }
    };

    return {
        handleMSALLoginSuccess,
        loading: loading || generateTokenMutation.isLoading,
        error: error || generateTokenMutation.error?.message
    };
}; 